import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, LOAD_TYPES, applyLightingDemand, applyFixedApplianceDemand,
  applyRangeDemand, applyDryerDemand, type LoadEntry,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const PanelScheduleCalc = ({ onSendToService, onSave }: { onSendToService?: (va: number) => void; onSave?: SaveFn }) => {
  const [panelType, setPanelType] = useState<"residential" | "commercial">("residential");
  const [voltage, setVoltage] = useState(defaults.voltage);
  const [phases, setPhases] = useState<string>(defaults.phases);
  const [sqft, setSqft] = useState("");
  const [entries, setEntries] = useState<LoadEntry[]>([
    { id: crypto.randomUUID(), description: "Small Appliance Circuits (2)", va: "3000", type: "small_appliance" },
    { id: crypto.randomUUID(), description: "Laundry Circuit", va: "1500", type: "laundry" },
  ]);

  const addLoad = () => setEntries((prev) => [...prev, { id: crypto.randomUUID(), description: "", va: "", type: "other" }]);
  const removeLoad = (id: string) => setEntries((prev) => prev.length > 1 ? prev.filter((e) => e.id !== id) : prev);
  const updateLoad = (id: string, field: keyof Omit<LoadEntry, "id">, value: string) => setEntries((prev) => prev.map((e) => e.id === id ? { ...e, [field]: value } : e));

  const V = parseFloat(voltage);
  const ph = parseInt(phases);
  const phaseDivisor = ph === 3 ? 1.732 : 1;
  const area = parseFloat(sqft) || 0;
  const lightingVaPerSqft = panelType === "residential" ? 3 : 3.5;
  const generalLightingVA = area * lightingVaPerSqft;

  const groupByType = (type: string) => entries.filter((e) => e.type === type);
  const sumVA = (list: LoadEntry[]) => list.reduce((s, e) => s + (parseFloat(e.va) || 0), 0);

  const lightingTotal = generalLightingVA + sumVA(groupByType("general"));
  const smallAppTotal = sumVA(groupByType("small_appliance"));
  const laundryTotal = sumVA(groupByType("laundry"));
  const lightingCombined = lightingTotal + smallAppTotal + laundryTotal;
  const lightingDemand = applyLightingDemand(lightingCombined);

  const fixedApps = groupByType("fixed_appliance");
  const fixedAppDemand = applyFixedApplianceDemand(sumVA(fixedApps), fixedApps.length);
  const ranges = groupByType("range");
  const rangeDemand = applyRangeDemand(sumVA(ranges), ranges.length);
  const dryers = groupByType("dryer");
  const dryerDemand = applyDryerDemand(sumVA(dryers), dryers.length);
  const hvacTotal = sumVA(groupByType("hvac"));
  const motorEntries = groupByType("motor");
  const motorVAs = motorEntries.map((e) => parseFloat(e.va) || 0);
  const largestMotor = Math.max(0, ...motorVAs);
  const motorTotal = motorVAs.reduce((s, v) => s + v, 0);
  const motorDemand = motorTotal > 0 ? motorTotal + largestMotor * 0.25 : 0;
  const otherTotal = sumVA(groupByType("other"));

  const totalDemandVA = lightingDemand + fixedAppDemand + rangeDemand + dryerDemand + hvacTotal + motorDemand + otherTotal;
  const totalConnectedVA = lightingCombined + sumVA(fixedApps) + sumVA(ranges) + sumVA(dryers) + hvacTotal + motorTotal + otherTotal;
  const demandAmps = totalDemandVA > 0 ? totalDemandVA / (V * phaseDivisor) : 0;

  const STD_MAINS = [60, 100, 125, 150, 200, 225, 400, 600, 800, 1000, 1200, 1600, 2000];
  const minMain = STD_MAINS.find((s) => s >= demandAmps) ?? demandAmps;
  const hasInput = totalDemandVA > 0;
  const demandFactor = totalConnectedVA > 0 ? (totalDemandVA / totalConnectedVA) * 100 : 0;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">NEC Article 220 — Calculate service/panel size from connected loads with demand factors.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Panel Type</label><Select value={panelType} onValueChange={(v: "residential" | "commercial") => setPanelType(v)}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Floor Area (sq ft)</label><Input type="number" inputMode="numeric" value={sqft} onChange={(e) => setSqft(e.target.value)} placeholder="e.g. 2000" className="bg-secondary border-border font-mono-data text-lg" /></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label><Select value={voltage} onValueChange={setVoltage}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["120/240","120/208","277/480"].map(v => <SelectItem key={v} value={v.split("/")[1]}>{v}V</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phases</label><Select value={phases} onValueChange={setPhases}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">1Φ</SelectItem><SelectItem value="3">3Φ</SelectItem></SelectContent></Select></div>
      </div>

      {area > 0 && (
        <div className="trade-card border-accent/40 bg-accent/5 animate-fade-in">
          <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">General Lighting (NEC 220.12)</span><span className="font-mono-data text-sm font-bold text-foreground">{area.toLocaleString()} ft² × {lightingVaPerSqft} = <span className="text-primary">{generalLightingVA.toLocaleString()} VA</span></span></div>
        </div>
      )}

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Load Schedule</h3>

      {entries.map((entry) => (
        <div key={entry.id} className="space-y-1.5 bg-card border border-border rounded-lg p-2.5">
          <div className="flex gap-2">
            <div className="flex-1">
              <Select value={entry.type} onValueChange={(v) => updateLoad(entry.id, "type", v)}>
                <SelectTrigger className="bg-secondary border-border text-xs h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{LOAD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}><span>{t.label}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button type="button" onClick={() => removeLoad(entry.id)} className="h-8 w-8 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors shrink-0 text-xs" disabled={entries.length === 1}>✕</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={entry.description} onChange={(e) => updateLoad(entry.id, "description", e.target.value)} placeholder="Description…" className="bg-secondary border-border text-xs h-8" />
            <Input type="number" inputMode="numeric" value={entry.va} onChange={(e) => updateLoad(entry.id, "va", e.target.value)} placeholder="VA" className="bg-secondary border-border font-mono-data text-xs h-8" />
          </div>
          <p className="text-[9px] text-muted-foreground">{LOAD_TYPES.find((t) => t.value === entry.type)?.demandNote}</p>
        </div>
      ))}

      <button type="button" onClick={addLoad} className="w-full h-9 rounded-md border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors">+ Add Load</button>

      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-muted/40">
            <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">Demand Calculation Breakdown</span>
            <div className="space-y-1 text-xs font-mono-data">
              {lightingCombined > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Lighting + SA + Laundry (220.42)</span><span><span className="text-muted-foreground line-through mr-2">{lightingCombined.toLocaleString()}</span><span className="text-foreground font-bold">{Math.round(lightingDemand).toLocaleString()} VA</span></span></div>}
              {fixedApps.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Fixed Appliances (220.53){fixedApps.length >= 4 ? " ×75%" : ""}</span><span className="text-foreground font-bold">{Math.round(fixedAppDemand).toLocaleString()} VA</span></div>}
              {ranges.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Range/Oven (220.55 Col C)</span><span className="text-foreground font-bold">{Math.round(rangeDemand).toLocaleString()} VA</span></div>}
              {dryers.length > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Dryer (220.54)</span><span className="text-foreground font-bold">{Math.round(dryerDemand).toLocaleString()} VA</span></div>}
              {hvacTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">HVAC/Heat (220.60)</span><span className="text-foreground font-bold">{Math.round(hvacTotal).toLocaleString()} VA</span></div>}
              {motorDemand > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Motors (125% largest)</span><span className="text-foreground font-bold">{Math.round(motorDemand).toLocaleString()} VA</span></div>}
              {otherTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Other (100%)</span><span className="text-foreground font-bold">{Math.round(otherTotal).toLocaleString()} VA</span></div>}
              <div className="flex justify-between font-bold border-t border-border pt-1.5 mt-1.5 text-sm"><span>Total Demand</span><span className="text-primary">{Math.round(totalDemandVA).toLocaleString()} VA</span></div>
            </div>
          </div>

          <div className="trade-card border-primary/40 bg-primary/5">
            <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Service / Main Breaker</span><span className="font-mono-data text-xl text-primary font-bold">{minMain}A</span></div>
            <p className="text-[10px] text-muted-foreground mt-1">Demand: {fmt(demandAmps, 1)}A @ {V}V {ph}Φ</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="trade-card"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Connected</span><span className="font-mono-data text-sm font-bold">{Math.round(totalConnectedVA).toLocaleString()} VA</span></div>
            <div className="trade-card"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Demand</span><span className="font-mono-data text-sm font-bold text-primary">{Math.round(totalDemandVA).toLocaleString()} VA</span></div>
            <div className="trade-card"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Factor</span><span className="font-mono-data text-sm font-bold">{fmt(demandFactor, 1)}%</span></div>
          </div>

          <div className="trade-card border-muted/40">
            <div className="flex justify-between items-center"><span className="text-xs text-muted-foreground">Demand Amps</span><span className="font-mono-data text-lg font-bold text-foreground">{fmt(demandAmps, 1)} A</span></div>
            <div className="w-full bg-secondary/50 rounded-full h-3 mt-2 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${demandAmps / minMain > 0.9 ? "bg-destructive" : demandAmps / minMain > 0.7 ? "bg-safety" : "bg-primary"}`} style={{ width: `${Math.min((demandAmps / minMain) * 100, 100)}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>0A</span><span>{minMain}A panel</span></div>
          </div>
        </div>
      )}

      {hasInput && onSendToService && (
        <button
          onClick={() => onSendToService(Math.round(totalDemandVA))}
          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm shadow-sm hover:bg-primary/90 transition-colors"
        >
          → Size Service Entrance ({Math.round(totalDemandVA).toLocaleString()} VA)
        </button>
      )}

      {hasInput && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "panel-schedule", label: `${panelType} Panel — ${Math.round(totalDemandVA).toLocaleString()} VA demand`,
            inputs: { type: panelType, sqft: sqft || "—", voltage: `${voltage}V`, phases: `${phases}Φ` },
            results: [
              { label: "Connected", value: Math.round(totalConnectedVA).toLocaleString(), unit: "VA" },
              { label: "Demand", value: Math.round(totalDemandVA).toLocaleString(), unit: "VA" },
              { label: "Demand Amps", value: fmt(demandAmps, 1), unit: "A" },
              { label: "Main Breaker", value: String(minMain), unit: "A" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ Based on NEC Article 220 demand factors. Always verify with local AHJ.</p>
    </div>
  );
};
